import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { formatDate } from '@angular/common';
import { AgentService, AuditTrailEntry, AuditTrailResponse } from '../services/agent.service';

@Component({
  selector: 'app-agent-audit-trail',
  standalone: false,
  templateUrl: './agent-audit-trail.component.html',
  styleUrls: ['./agent-audit-trail.component.css']
})
export class AgentAuditTrailComponent implements OnChanges {
  @Input() isVisible = false;
  @Output() close = new EventEmitter<void>();

  records: AuditTrailEntry[] = [];
  isLoading = false;
  currentPage = 1;
  pageSize = 10;
  totalCount = 0;

  fromDate: Date | null = null;
  toDate: Date | null = null;
  expandedRows = new Set<number>();

  constructor(private agentService: AgentService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isVisible'] && this.isVisible) {
      this.currentPage = 1;
      this.loadAuditTrail();
    }
  }

  loadAuditTrail(): void {
    this.isLoading = true;
    const offset = (this.currentPage - 1) * this.pageSize;
    const fromStr = this.fromDate
      ? formatDate(this.fromDate, 'yyyy-MM-ddTHH:mm:ss', 'en-US')
      : undefined;
    const toStr = this.toDate
      ? formatDate(this.toDate, 'yyyy-MM-ddT23:59:59', 'en-US')
      : undefined;

    this.agentService.getAuditTrail(fromStr, toStr, this.pageSize, offset).subscribe({
      next: (response: AuditTrailResponse) => {
        this.records = response.records;
        this.totalCount = response.total_count;
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  onFromDateChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.fromDate = input.value ? this.parseLocalDate(input.value) : null;
    this.currentPage = 1;
    this.loadAuditTrail();
  }

  onToDateChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.toDate = input.value ? this.parseLocalDate(input.value) : null;
    this.currentPage = 1;
    this.loadAuditTrail();
  }

  // Parse YYYY-MM-DD as local midnight to avoid UTC offset shifting the date
  private parseLocalDate(value: string): Date {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  toggleRow(id: number): void {
    if (this.expandedRows.has(id)) {
      this.expandedRows.delete(id);
    } else {
      this.expandedRows.add(id);
    }
  }

  isExpanded(id: number): boolean {
    return this.expandedRows.has(id);
  }

  closeModal(): void {
    this.expandedRows.clear();
    this.close.emit();
  }

  get totalPages(): number {
    return Math.ceil(this.totalCount / this.pageSize);
  }

  get showingFrom(): number {
    if (this.totalCount === 0) return 0;
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get showingTo(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalCount);
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const total = this.totalPages;
    const current = this.currentPage;
    const delta = 2;
    for (let i = Math.max(1, current - delta); i <= Math.min(total, current + delta); i++) {
      pages.push(i);
    }
    return pages;
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) return;
    this.currentPage = page;
    this.loadAuditTrail();
  }

  getActionStyle(action: string): { color: string; background: string; border: string } {
    const a = (action || '').toUpperCase();
    if (a === 'PUBLISH') return { color: '#7242cf', background: 'rgba(114,66,207,0.08)', border: '#7242cf' };
    if (a === 'DRAFT') return { color: '#D97706', background: 'rgba(254,243,199,0.5)', border: '#D97706' };
    if (a === 'EDIT' || a === 'UPDATE') return { color: '#7242cf', background: '#fff', border: '#7242cf' };
    if (a === 'DELETE') return { color: '#EF4444', background: 'rgba(254,226,226,0.5)', border: '#EF4444' };
    if (a === 'CREATE') return { color: '#10B981', background: 'rgba(209,250,229,0.5)', border: '#10B981' };
    return { color: '#6B7280', background: '#f3f4f6', border: '#d1d5db' };
  }

  getStateKeys(state: Record<string, any> | null): string[] {
    if (!state) return [];
    return Object.keys(state);
  }

  formatKey(key: string): string {
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .replace(/^\s/, '')
      .replace(/\b\w/g, c => c.toUpperCase());
  }

  formatValue(val: any): string {
    if (val === null || val === undefined) return '—';
    if (typeof val === 'boolean') return val ? 'Yes' : 'No';
    if (typeof val === 'object') return JSON.stringify(val);
    return String(val);
  }

  getSectionValue(state: Record<string, any>, key: string): any {
    return state[key];
  }

  isObjectValue(val: any): boolean {
    return val !== null && typeof val === 'object' && !Array.isArray(val);
  }

  isArrayValue(val: any): boolean {
    return Array.isArray(val);
  }

  getObjectEntries(val: Record<string, any>): { k: string; v: any }[] {
    return Object.entries(val).map(([k, v]) => ({ k, v }));
  }

  // Returns union of keys from both states so each section appears once
  getMergedKeys(prev: Record<string, any> | null, curr: Record<string, any> | null): string[] {
    const keys = new Set<string>([
      ...Object.keys(prev || {}),
      ...Object.keys(curr || {})
    ]);
    return Array.from(keys);
  }
}
